import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import { View } from 'react-native';
import { usePermission, type PermissionStatus } from '@/features/settings/hooks';
import type { PermissionEntry } from '../registry';

interface SettingPermissionRowProps {
  entry: PermissionEntry;
  isLast?: boolean;
}

const STATUS_LABEL: Record<PermissionStatus, string> = {
  granted: 'Granted',
  denied: 'Denied',
  undetermined: 'Not yet asked',
};

interface RowAction {
  label: string;
  /** A fuller accessible name, since a bare "Manage" lacks context for a reader. */
  accessibilityLabel: string;
  onPress: () => Promise<void>;
}

/**
 * The one action a row offers for a given status, or null when there is nothing
 * useful to do:
 *  - undetermined → prompt for permission.
 *  - granted      → jump to settings to manage it, but only where a deep-link exists.
 *  - denied       → jump to settings to re-enable, but only where a deep-link exists;
 *                   on web the row shows `deniedGuidance` instead (no action).
 */
function resolveAction(
  status: PermissionStatus,
  canOpenSettings: boolean,
  label: string,
  request: () => Promise<void>,
  openSettings: () => Promise<void>,
): RowAction | null {
  if (status === 'undetermined') {
    return {
      label: `Allow ${label}`,
      accessibilityLabel: `Allow ${label} access`,
      onPress: request,
    };
  }
  if (!canOpenSettings) return null;
  if (status === 'granted') {
    return {
      label: 'Manage',
      accessibilityLabel: `Manage ${label} permission in settings`,
      onPress: openSettings,
    };
  }
  return {
    label: 'Open Settings',
    accessibilityLabel: `Open settings to enable ${label}`,
    onPress: openSettings,
  };
}

/**
 * A status-aware OS-permission row. Reads live grant status from `usePermission`
 * and offers the right next action: request when never asked, else bounce out to
 * the OS Settings app. Where the platform has no settings deep-link (web), a
 * denied permission renders written guidance instead of a dead-end button.
 */
export function SettingPermissionRow({
  entry,
  isLast = false,
}: SettingPermissionRowProps) {
  const { status, request, openSettings, canOpenSettings, deniedGuidance } =
    usePermission(entry.permissionKey);

  const action = resolveAction(
    status,
    canOpenSettings,
    entry.label,
    request,
    openSettings,
  );
  const showGuidance =
    status === 'denied' && !canOpenSettings && !!deniedGuidance;

  // Fold name + status (+ guidance, when shown) into one reader announcement, so
  // the grouped `accessible` label never swallows the guidance it replaces.
  const groupLabel = showGuidance
    ? `${entry.label}, ${STATUS_LABEL[status]}. ${deniedGuidance}`
    : `${entry.label}, ${STATUS_LABEL[status]}`;

  return (
    <View
      testID={`permission-${entry.permissionKey}`}
      className={`flex-row items-center py-4 ${!isLast ? 'border-b border-border' : ''}`}
    >
      <View className="w-8 items-center mr-3">
        <Ionicons name={entry.icon as never} size={22} className="text-foreground" />
      </View>
      {/* Group name + status so a reader hears "Location, Granted" as one item. */}
      <View className="flex-1 pr-3" accessible accessibilityLabel={groupLabel}>
        <Text className="text-base text-foreground">{entry.label}</Text>
        <Text className="text-sm text-muted-foreground">
          {STATUS_LABEL[status]}
        </Text>
        {showGuidance && (
          <Text
            testID={`permission-${entry.permissionKey}-guidance`}
            className="text-sm text-muted-foreground mt-1"
          >
            {deniedGuidance}
          </Text>
        )}
      </View>
      {action && (
        <Button
          testID={`permission-${entry.permissionKey}-action`}
          variant="outline"
          size="sm"
          accessibilityLabel={action.accessibilityLabel}
          onPress={() => {
            void action.onPress();
          }}
        >
          {action.label}
        </Button>
      )}
    </View>
  );
}
